'use client';

import { ChangeEvent } from 'react';

import { useTranslations } from 'next-intl';

import AddBusinessInformation from 'components/payment/components/AddBusinessInformation';
import useUser from 'hooks/useUser';
import Input from 'ui/Input';
import Loading from 'ui/Loader';
import TenantThemeButton from 'ui/TenantThemeButton';

interface ErrorObject {
  [key: string]: string;
}

type PayingMemberInfoProps = {
  userExistResponseLoading: boolean;
  userExists: boolean;
  isLoginLoading: boolean;
  handleLogin: () => void;
  checkIfFieldIsDisplayable: (fieldName: string) => boolean;
  name: string;
  handleChangeName: (e: ChangeEvent<HTMLInputElement>) => void;
  errors: ErrorObject;
  email: string;
  handleChangeEmail: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlurEmail: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const PayingMemberInfo = ({
  userExistResponseLoading,
  userExists,
  isLoginLoading,
  handleLogin,
  checkIfFieldIsDisplayable,
  name,
  handleChangeName,
  errors,
  email,
  handleChangeEmail,
  handleBlurEmail,
}: PayingMemberInfoProps) => {
  const tCheckout = useTranslations('checkout');
  const tAuth = useTranslations('id.auth');

  const { user } = useUser();

  const handleEmailChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChangeEmail(e); // call existing handleChangeEmail
  };

  const handleEmailBlur = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleBlurEmail(e); // call existing handleBlurEmail
  };

  // If the user is not logged in and the memberType field is displayed, show the AddBusinessInformation component
  const shouldShowCompanyInformation = checkIfFieldIsDisplayable('memberType') && !user;

  return (
    <>
      {shouldShowCompanyInformation && <AddBusinessInformation />}
      <div className="sm:col-span-6 flex flex-col gap-y-1">
        <label className="font-bold text-sm">{tCheckout('name').toLocaleUpperCase()}</label>
        <Input
          type="text"
          name="name"
          id="name"
          placeholder={tCheckout('namePlaceholder')}
          value={name}
          onChange={handleChangeName}
          errorMessage={errors.name}
          disabled={user ? true : false}
        />
      </div>
      <div className="sm:col-span-6 flex flex-col gap-y-1">
        <label className="font-bold text-sm">{tCheckout('loginEmail').toLocaleUpperCase()}</label>
        <Input
          type="email"
          name="email"
          id="email"
          dataTestId="loginEmail"
          placeholder={tCheckout('loginEmailPlaceholder')}
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          errorMessage={errors.email}
          disabled={user ? true : false}
        />
        {userExists && !userExistResponseLoading && (
          <div className="sm:col-span-6">
            <div className="flex w-full items-center gap-2 text-sm mt-2">
              <div className="text-red w-2/3">{tCheckout('loginToContinue.helpText')}</div>
              <div className="w-1/3">
                <TenantThemeButton
                  type="button"
                  disabled={isLoginLoading}
                  onClick={handleLogin}
                  size="wide"
                >
                  {isLoginLoading ? <Loading /> : tAuth('signin.title')}
                </TenantThemeButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {user && user?.companyName && (
        <div className="sm:col-span-6 flex flex-col gap-y-1">
          <label className="font-bold text-sm">
            {tCheckout('companyName').toLocaleUpperCase()}
          </label>
          <Input
            type="text"
            name="companyName"
            id="companyName"
            placeholder={tCheckout('companyNamePlaceholder')}
            defaultValue={user?.companyName}
            disabled={true}
          />
        </div>
      )}
    </>
  );
};
export default PayingMemberInfo;
